/**
 * Per-platform sync status for the dashboard strip.
 *
 * Reads media_platform_accounts directly (RLS scopes it to the caller's
 * workspaces) and keeps only LIVE accounts — a demo fixture's fake
 * last_synced_at would paint a green pill over a platform nobody connected.
 *
 * Deliberately NOT the stored token_status column: that field is written once
 * and never re-checked, and it has claimed "active" over a dead token before
 * (prod XPENG, OAuthException 190). The honest failure signal is sync_error —
 * what actually happened last time the system tried.
 *
 * The freshness number comes from the METRICS, not from last_synced_at. That
 * column is stamped only by the queue worker (sync-worker.ts), so a sync run
 * any other way leaves it behind and the dashboard reports stale data that is
 * in fact minutes old — which is exactly what happened on 2026-09-14, with
 * metrics through that day sitting under a strip saying "stale 4d". Settings
 * already reads the metric frontier for its own row; this reads the same thing
 * so the two pages cannot disagree.
 *
 * @module services/dashboard/syncStatusService
 */

import { getSupabaseClient } from "@/config";
import type { DbPlatform } from "@/types/dbEnums";
import { logError } from "@/services/logger";
import type { PlatformSyncStatus } from "./dashboardEvents";

export interface SyncStatusResult {
  success: boolean;
  data?: {
    statuses: PlatformSyncStatus[];
    /** Live account ids, for the Sync-now button to enqueue. */
    accounts: Array<{ id: string; platform: string }>;
  };
  error?: string;
}

export const fetchSyncStatuses = async (workspaceId: string): Promise<SyncStatusResult> => {
  try {
    const { data, error } = await getSupabaseClient()
      .from("media_platform_accounts")
      .select("id, platform, last_synced_at, sync_error, account_type")
      .eq("workspace_id", workspaceId);
    if (error) return { success: false, error: error.message };

    const rows = (data ?? []) as Array<{
      id: string;
      // The enum, not `string`: widening it here is what let an arbitrary
      // string reach `.eq("platform", ...)` on an enum column below.
      platform: DbPlatform;
      last_synced_at: string | null;
      sync_error: string | null;
      account_type: string | null;
    }>;
    const live = rows.filter((r) => r.account_type !== "demo_data");

    // One tiny query per platform rather than one big read of every metric row:
    // an unpaged select truncates at 1000 rows with HTTP 200 and no error, and
    // a truncated read here would silently report the wrong newest date.
    const platforms = [...new Set(live.map((r) => r.platform))];
    const frontiers = new Map<string, string | null>();
    await Promise.all(
      platforms.map(async (platform) => {
        const { data: row, error: frontierError } = await getSupabaseClient()
          .from("media_ad_set_metrics")
          .select("date")
          .eq("workspace_id", workspaceId)
          .eq("platform", platform)
          .order("date", { ascending: false })
          .limit(1);
        // A failed read is not an empty one: leaving it null would paint the
        // platform "never synced" over data nobody managed to look at.
        if (frontierError) throw new Error(frontierError.message);
        frontiers.set(platform, (row?.[0]?.date as string | undefined) ?? null);
      }),
    );

    return {
      success: true,
      data: {
        statuses: live.map((r) => ({
          platform: r.platform,
          lastSyncedAt: r.last_synced_at,
          dataThrough: frontiers.get(r.platform) ?? null,
          syncError: r.sync_error,
        })),
        accounts: live.map((r) => ({ id: r.id, platform: r.platform })),
      },
    };
  } catch (e) {
    logError("Dashboard", "fetchSyncStatuses failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load sync status" };
  }
};
